// stahlia-frontend/src/pages/warehouse/InventurPage.tsx
// Inventur-Uebersicht (Spec-Merkliste ③): Sessionliste + Leiter-Karte "Session starten".
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { useInventurSessions, useCreateInventur, type InventurSession } from '@/api/inventur'
import { usePlaceRows } from '@/api/warehouse'
import { useAuth } from '@/api/auth'
import { validateScope, type InventurMode } from '@/lib/inventur-form'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { InventurImport } from './InventurImport'

function ModeBadge({ mode }: { mode: InventurMode }) {
  const label = mode === 'regal' ? 'Regal' : mode === 'produkt' ? 'Produkt' : 'Voll'
  return <span className="text-xs font-semibold">{label}</span>
}

function StatusBadge({ status }: { status: InventurSession['status'] }) {
  return <span className="inline-flex items-center gap-1.5 text-xs"><span aria-hidden="true">●</span>{status === 'abgeschlossen' ? 'Abgeschlossen' : 'Offen'}</span>
}

function scopeText(session: InventurSession): string {
  if (session.mode === 'regal') return (session.scope.row_codes ?? []).join(', ') || '—'
  if (session.mode === 'produkt') return `${(session.scope.merchant_skus ?? []).length} Artikel`
  return 'alle Plätze'
}

export default function InventurPage() {
  const { warehouse } = useAuth()
  const navigate = useNavigate()
  const sessions = useInventurSessions()
  const placeRows = usePlaceRows()
  const create = useCreateInventur()

  const [mode, setMode] = useState<InventurMode>('regal')
  const [ausListe, setAusListe] = useState(false)
  const [rowCodes, setRowCodes] = useState<Set<string>>(new Set())
  const [skusText, setSkusText] = useState('')
  const [note, setNote] = useState('')

  function toggleRow(rc: string) {
    setRowCodes((prev) => {
      const next = new Set(prev)
      if (next.has(rc)) next.delete(rc); else next.add(rc)
      return next
    })
  }

  async function onCreate() {
    const scope = mode === 'regal'
      ? { row_codes: [...rowCodes] }
      : mode === 'produkt'
        ? { merchant_skus: skusText.split('\n').map((s) => s.trim()).filter(Boolean) }
        : {}
    const err = validateScope(mode, scope)
    if (err) { toast.error(err); return }
    try {
      const r = await create.mutateAsync({ mode, scope, note: note || undefined })
      toast.success('Inventur-Session gestartet')
      navigate(`/warehouse/inventur/${r.id}`)
    } catch { /* Fehler-Toast kommt aus useCreateInventur().onError */ }
  }

  const rows = sessions.data ?? []

  return (
    <div className="p-4 space-y-4 max-w-7xl min-w-0 w-full mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-xl font-bold">Inventur</h1>
        <div className="text-sm text-muted">{rows.length} Session(en)</div>
      </div>

      {warehouse.lead && (
        <div className="rounded-xl border border-line bg-panel p-4 space-y-3">
          <div className="flex items-center gap-2">
            <div className="font-semibold">Session starten</div>
          </div>

          <div className="flex gap-2 border-b border-line pb-3">
            <Button variant={ausListe ? 'default' : 'outline'} onClick={() => setAusListe(true)} aria-pressed={ausListe}>Aus Artikelliste</Button>
            <Button variant={!ausListe ? 'default' : 'outline'} onClick={() => setAusListe(false)} aria-pressed={!ausListe}>Regal / Produkt / Voll</Button>
          </div>
          {ausListe ? <InventurImport /> : <>
          <div className="flex flex-wrap gap-4 items-start">
            <label className="text-sm space-y-1">
              <span>Modus</span>
              <select
                className="block border border-line rounded-md px-2 py-1.5 text-sm bg-panel"
                value={mode}
                onChange={(e) => setMode(e.target.value as InventurMode)}
              >
                <option value="regal">Regal (Reihen)</option>
                <option value="produkt">Produkt (Artikel)</option>
                <option value="voll">Voll (alle Plätze)</option>
              </select>
            </label>

            {mode === 'regal' && (
              <div className="text-sm space-y-1">
                <span>Regalreihen</span>
                <div className="flex flex-wrap gap-1 max-w-xl">
                  {(placeRows.data ?? []).map((r) => (
                    <button
                      key={r.rowCode}
                      type="button"
                      onClick={() => toggleRow(r.rowCode)}
                      className={
                        'px-2 py-1 rounded-md text-xs font-mono border ' +
                        (rowCodes.has(r.rowCode)
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'border-line text-muted hover:bg-panel-2')
                      }
                    >
                      {r.rowCode} ({r.count})
                    </button>
                  ))}
                  {(placeRows.data ?? []).length === 0 && (
                    <span className="text-muted">Keine Reihen vorhanden.</span>
                  )}
                </div>
              </div>
            )}

            {mode === 'produkt' && (
              <label className="text-sm space-y-1 w-72">
                <span>Händler-Artikelnummern (eine je Zeile)</span>
                <Textarea
                  className="w-full h-28 font-mono text-xs"
                  value={skusText}
                  onChange={(e) => setSkusText(e.target.value)}
                  placeholder={'RGPL027\nRGPL028'}
                />
              </label>
            )}

            <label className="text-sm space-y-1 w-64">
              <span>Notiz (optional)</span>
              <Textarea className="w-full h-16" value={note} onChange={(e) => setNote(e.target.value)} />
            </label>
          </div>

          <Button
            onClick={() => void onCreate()}
            disabled={create.isPending}
          >
            {create.isPending ? 'Starte…' : 'Session starten'}
          </Button>
          </>}
        </div>
      )}

      {sessions.isError && <p role="alert" className="text-sm text-destructive">Inventurliste konnte nicht geladen werden: {sessions.error.message}. <Button variant="outline" onClick={() => void sessions.refetch()}>Erneut laden</Button></p>}

      <div className="overflow-x-auto rounded-xl border border-line">
        <table className="w-full text-sm">
          <thead className="bg-panel text-left">
            <tr>
              <th className="p-2">#</th>
              <th className="p-2">Modus</th>
              <th className="p-2">Scope</th>
              <th className="p-2">Status</th>
              <th className="p-2">Notiz</th>
              <th className="p-2">Angelegt</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.id} className="border-t border-line hover:bg-primary-soft">
                <td className="p-2"><Link className="text-primary underline" to={`/warehouse/inventur/${s.id}`}>#{s.id}</Link></td>
                <td className="p-2"><ModeBadge mode={s.mode} /></td>
                <td className="p-2 text-muted">{scopeText(s)}</td>
                <td className="p-2"><StatusBadge status={s.status} /></td>
                <td className="p-2 text-muted">{s.note ?? '—'}</td>
                <td className="p-2 text-muted">{new Date(s.createdAt).toLocaleString('de-DE')} {s.createdByName ?? ''}</td>
              </tr>
            ))}
            {rows.length === 0 && !sessions.isLoading && (
              <tr><td colSpan={6} className="p-4 text-center text-muted">Keine Inventur-Sessions.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
